// "Dream Bigger" — the raise-money chapter. Teaches the three ways to fund a goal
// bigger than your cash, then makes the choice real:
//   • SAVE   — patient, free, compounds. Sets a Dream goal; keep depositing.
//   • BORROW — cash now, repaid weekly WITH interest (finite cost).
//   • PITCH  — cash now, never repaid, but an investor takes a % of sales forever.
// Borrow/Pitch immediately buy the vending machine → the kid's first passive income.
//
// Re-skinned to the neo-fintech system; mechanics unchanged.

import { useState } from 'react';
import {
  businessWeeklyResult,
  repFactor,
  weeklyLoanPayment,
  findBusiness,
  findAsset,
} from '@biz-kid/engine';
import { builderContent } from '@biz-kid/engine/content/builder';
import { useDispatch, useGameState } from '@biz-kid/store';
import { Card, money } from '../../ui/kit.js';
import { StoryShell, Coach, StoryChoice, PreviewRows, choiceGrid } from '../../chapters/storyKit.js';

export type RaiseMoneyProps = { onComplete: () => void };

const ASSET_ID = 'vendingMachine';
const INVESTOR_SHARE = 0.25;
const SAVE_PER_WEEK = 50;
const COMPARE_WEEKS = 20;

const INTRO_LINES = [
  'See that Course Creator? B$500.',
  'Here’s the magic: it pays you B$35 every single week — whether you’re dropping new stuff or fast asleep.',
  'That’s passive income. Money that shows up without you. But you’ve only got the cash you’ve got — so how do we find B$500?',
];

type Step =
  | { kind: 'intro' }
  | { kind: 'choose' }
  | { kind: 'reply'; line: string; after: () => void };

export function RaiseMoney({ onComplete }: RaiseMoneyProps) {
  const dispatch = useDispatch();
  const cash = useGameState((s) => s.wallet.cash);
  const business = useGameState((s) => s.businesses[0]);
  const reputation = useGameState((s) => s.profile.reputation);
  const [step, setStep] = useState<Step>({ kind: 'intro' });

  const content = builderContent;
  const asset = findAsset(content, ASSET_ID);
  const cost = asset?.cost ?? 500;
  const yieldPerWeek = asset?.weeklyYield ?? 35;

  // Estimate the stand's weekly revenue so the investor's cut is concrete.
  const def = business ? findBusiness(content, business.defId) : undefined;
  const estRevenue =
    business && def
      ? businessWeeklyResult({
          baseDemand: def.baseDemand,
          price: business.price,
          sweetPrice: def.sweetPrice,
          marketingSpend: business.marketingSpend,
          qualityFactor: def.qualityFactorDefault,
          repFactor: repFactor(reputation),
          capacity: def.capacity,
          cogsPerUnit: def.cogsPerUnit,
          fixedPerWeek: def.fixedPerWeek,
        }).revenue
      : 60;

  // Loan maths.
  const loanPayment = weeklyLoanPayment(
    cost,
    content.tuning.loan.ratePerWeek,
    content.tuning.loan.defaultTermWeeks,
  );
  const loanTotal = loanPayment * content.tuning.loan.defaultTermWeeks;
  const loanInterest = loanTotal - cost;

  // Pitch maths.
  const investorWeekly = estRevenue * INVESTOR_SHARE;
  const investorOverWindow = investorWeekly * COMPARE_WEEKS;

  // Save maths (simple, ignores the small interest tailwind — keeps it honest & clear).
  const weeksToSave = Math.max(1, Math.ceil(cost / SAVE_PER_WEEK));

  const buyMachine = () => {
    const e = dispatch({ kind: 'BuyAsset', defId: ASSET_ID });
    if (e) console.warn('BuyAsset failed:', e);
  };

  const chooseSave = () => {
    dispatch({ kind: 'SetDreamGoal', goalId: ASSET_ID, targetAmount: cost });
    setStep({
      kind: 'reply',
      line: 'The patient road. Tuck money in the jar each week and it even grows a little on its own. Come back when it’s full.',
      after: onComplete,
    });
  };

  const chooseBorrow = () => {
    const e = dispatch({ kind: 'TakeLoan', principal: cost });
    if (e) {
      setStep({ kind: 'reply', line: `Hmm — ${e.message} Let’s try another way.`, after: () => setStep({ kind: 'choose' }) });
      return;
    }
    buyMachine();
    setStep({
      kind: 'reply',
      line: `Bought! It’s yours. Just remember — B$${loanPayment.toFixed(0)} leaves every week until the loan’s paid. Borrowed money always wants a little extra back.`,
      after: onComplete,
    });
  };

  const choosePitch = () => {
    const e = dispatch({ kind: 'AcceptInvestment', amount: cost, sharePct: INVESTOR_SHARE });
    if (e) {
      setStep({ kind: 'reply', line: `Hmm — ${e.message}`, after: () => setStep({ kind: 'choose' }) });
      return;
    }
    buyMachine();
    setStep({
      kind: 'reply',
      line: `Done — no debt to repay. But your investor now takes ${Math.round(
        INVESTOR_SHARE * 100,
      )}% of every sale, for as long as they’re in. Sharing the upside is the price of help today.`,
      after: onComplete,
    });
  };

  return (
    <StoryShell eyebrow="Dream Bigger · Raise money" maxWidth={600}>
      {step.kind === 'intro' && <Coach lines={INTRO_LINES} onDone={() => setStep({ kind: 'choose' })} />}

      {step.kind === 'choose' && (
        <>
          <Card>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--text-2)', lineHeight: 1.6 }}>
              You have <b style={{ color: 'var(--brand)' }}>{money(cash)}</b> · the machine costs{' '}
              <b style={{ color: 'var(--text)' }}>{money(cost)}</b> and pays{' '}
              <b style={{ color: 'var(--up)' }}>{money(yieldPerWeek)}/wk</b> forever.
            </p>
          </Card>
          <div style={choiceGrid}>
            <StoryChoice
              tone="fair"
              label="Save up"
              subtitle="The free, patient way."
              onClick={chooseSave}
              preview={
                <PreviewRows
                  rows={[
                    ['Put aside', `${money(SAVE_PER_WEEK)}/wk`],
                    ['Reach B$500 in', `~${weeksToSave} weeks`],
                    ['Extra cost', 'B$0 — it’s yours'],
                  ]}
                />
              }
            />
            <StoryChoice
              tone="warm"
              label="Borrow it"
              subtitle="Cash now. Pay it back with interest."
              onClick={chooseBorrow}
              preview={
                <PreviewRows
                  rows={[
                    ['Get now', money(cost)],
                    ['Pay weekly', `${money(loanPayment)} × ${content.tuning.loan.defaultTermWeeks}`],
                    ['Extra cost', `+${money(loanInterest)} interest`],
                  ]}
                />
              }
            />
            <StoryChoice
              tone="premium"
              label="Pitch an investor"
              subtitle="Cash now. Share your sales."
              onClick={choosePitch}
              preview={
                <PreviewRows
                  rows={[
                    ['Get now', money(cost)],
                    ['They take', `${Math.round(INVESTOR_SHARE * 100)}% of sales`],
                    [`~Cost over ${COMPARE_WEEKS}wk`, `${money(investorOverWindow)}+`],
                  ]}
                />
              }
            />
          </div>
        </>
      )}

      {step.kind === 'reply' && <Coach lines={[step.line]} onDone={step.after} />}
    </StoryShell>
  );
}
